import {
  ApiClient,
  ApiError,
  type ContainerStats,
  type ContainerSummary,
  type HostMetrics,
  type ImageSummary,
  type ProxyAggregateSnapshot,
  type ProxyHttpRouteSummary,
  type ProxyReplicaSnapshot,
  type VolumeSummary,
} from '../lib/api.js';
import {
  el,
  formatBytes,
  formatDateTime,
  formatPercent,
  formatRelativeTime,
  formatUptime,
  mount,
  statusPill,
  type PillKind,
} from '../lib/dom.js';

export interface MonitoringViewDeps {
  api: ApiClient;
  /** When false, the per-app "App replicas" sub-tab is hidden (no proxy config). */
  proxyMonitoring?: boolean;
}

type SubTab = 'host' | 'app' | 'containers' | 'images' | 'volumes';

export function renderMonitoring(deps: MonitoringViewDeps): HTMLElement {
  const subTabBar = el('div', { class: 'subtabs', attrs: { role: 'tablist' } });
  const content = el('div');
  let cleanupCurrent: (() => void) | null = null;
  // Host/containers/images/volumes are host-global; "App replicas" is the only
  // app-scoped pane and is shown only when the selected app declares a proxy.
  const subTabs: SubTab[] = deps.proxyMonitoring
    ? ['host', 'app', 'containers', 'images', 'volumes']
    : ['host', 'containers', 'images', 'volumes'];

  const setSub = (tab: SubTab) => {
    if (cleanupCurrent) {
      cleanupCurrent();
      cleanupCurrent = null;
    }
    for (const btn of subTabBar.querySelectorAll('button.subtab')) {
      const isSel = (btn as HTMLElement).dataset.subtab === tab;
      btn.setAttribute('aria-selected', String(isSel));
    }
    if (tab === 'host') {
      const view = renderHost(deps);
      cleanupCurrent = view.cleanup;
      mount(content, [view.el]);
    } else if (tab === 'app') {
      const view = renderAppReplicas(deps);
      cleanupCurrent = view.cleanup;
      mount(content, [view.el]);
    } else if (tab === 'containers') {
      const view = renderContainers(deps);
      cleanupCurrent = view.cleanup;
      mount(content, [view.el]);
    } else if (tab === 'images') {
      const view = renderImages(deps);
      cleanupCurrent = view.cleanup;
      mount(content, [view.el]);
    } else {
      const view = renderVolumes(deps);
      cleanupCurrent = view.cleanup;
      mount(content, [view.el]);
    }
  };

  const subTabLabels: Record<SubTab, string> = {
    host: 'Host',
    app: 'App replicas',
    containers: 'Containers',
    images: 'Images',
    volumes: 'Volumes',
  };
  for (const tab of subTabs) {
    subTabBar.append(
      el('button', {
        class: 'subtab',
        text: subTabLabels[tab],
        attrs: { type: 'button', role: 'tab', 'data-subtab': tab, 'aria-selected': false },
        on: { click: () => setSub(tab) },
      }),
    );
  }

  const root = el('div', { children: [subTabBar, content] });
  setSub('host');

  // When the parent unmounts this element, stop streams.
  const observer = new MutationObserver(() => {
    if (!root.isConnected && cleanupCurrent) {
      cleanupCurrent();
      cleanupCurrent = null;
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return root;
}

// ─── Host ────────────────────────────────────────────────────────

function renderHost(deps: MonitoringViewDeps): { el: HTMLElement; cleanup: () => void } {
  const grid = el('div', { class: 'metrics-grid' });
  const status = el('span', { class: 'who', text: 'Connecting…' });
  const heading = el('div', {
    class: 'toolbar',
    children: [el('h2', { text: 'Host', attrs: { style: 'margin:0' } }), el('span', { class: 'spacer' }), status],
  });
  const card = el('div', { class: 'card', children: [heading, grid] });

  const renderSnapshot = (m: HostMetrics) => {
    const memPct = m.memory.total > 0 ? (m.memory.used / m.memory.total) * 100 : 0;
    const swapPct = m.memory.swapTotal > 0 ? (m.memory.swapUsed / m.memory.swapTotal) * 100 : 0;
    const diskPct = m.disk.total > 0 ? (m.disk.used / m.disk.total) * 100 : 0;
    mount(grid, [
      gauge('CPU', m.cpu.usage, formatPercent(m.cpu.usage), `${m.cpu.cores} cores · load ${m.loadAvg.map((l) => l.toFixed(2)).join(' / ')}`),
      gauge('Memory', memPct, formatPercent(memPct), `${formatBytes(m.memory.used)} of ${formatBytes(m.memory.total)} · ${formatBytes(m.memory.available)} available`),
      gauge('Swap', swapPct, m.memory.swapTotal === 0 ? 'off' : formatPercent(swapPct), m.memory.swapTotal === 0 ? 'No swap configured' : `${formatBytes(m.memory.swapUsed)} of ${formatBytes(m.memory.swapTotal)}`),
      gauge('Disk', diskPct, formatPercent(diskPct), `${formatBytes(m.disk.used)} of ${formatBytes(m.disk.total)} · ${formatBytes(m.disk.free)} free · ${m.disk.path}`),
      infoTile('Network', `${formatBytes(m.net.rxRate)}/s ↓ · ${formatBytes(m.net.txRate)}/s ↑`, `${formatBytes(m.net.rxBytes)} received · ${formatBytes(m.net.txBytes)} sent`),
      infoTile('System', m.hostname, `Linux ${m.kernel} · uptime ${formatUptime(m.uptimeSeconds)}`),
    ]);
    status.textContent = `Live · ${formatDateTime(m.timestamp)}`;
  };

  const close = deps.api.streamHostMetrics({
    onSnapshot: renderSnapshot,
    onError: () => { status.textContent = 'Disconnected — retrying…'; },
  });

  return { el: card, cleanup: close };
}

function gauge(label: string, percent: number, big: string, sub: string): HTMLElement {
  const safe = Math.max(0, Math.min(100, percent));
  const tone =
    safe >= 90 ? 'critical' : safe >= 75 ? 'warning' : 'ok';
  const bar = el('div', { class: 'gauge-bar' });
  const fill = el('div', { class: `gauge-fill ${tone}`, attrs: { style: `width:${safe.toFixed(1)}%` } });
  bar.append(fill);
  return el('div', {
    class: 'metric-tile',
    children: [
      el('div', { class: 'metric-label', text: label }),
      el('div', { class: 'metric-big', text: big }),
      bar,
      el('div', { class: 'metric-sub', text: sub }),
    ],
  });
}

function infoTile(label: string, big: string, sub: string): HTMLElement {
  return el('div', {
    class: 'metric-tile',
    children: [
      el('div', { class: 'metric-label', text: label }),
      el('div', { class: 'metric-big', text: big }),
      el('div', { class: 'metric-sub', text: sub }),
    ],
  });
}

// ─── App replicas (blueeden-app proxy /api/metrics — P-4 companion) ──
//
// One row per replica with the in-process signals that can't be derived
// from container stats: event-loop p99, request p95/p99 + error rate, pg
// pool counters. The aggregate row at the top is the cluster-wide view.

function renderAppReplicas(deps: MonitoringViewDeps): { el: HTMLElement; cleanup: () => void } {
  const summary = el('div', { class: 'metrics-grid' });
  const list = el('div', { class: 'replica-list' });
  const status = el('span', { class: 'who', text: 'Connecting…' });
  const card = el('div', {
    class: 'card',
    children: [
      el('div', {
        class: 'toolbar',
        children: [
          el('h2', { text: 'App replicas', attrs: { style: 'margin:0' } }),
          el('span', { class: 'spacer' }),
          status,
        ],
      }),
      el('p', {
        class: 'help',
        text: 'Live in-process metrics scraped per replica from /api/metrics. Aggregates assume sticky sessions distribute load roughly evenly.',
      }),
      summary,
      list,
    ],
  });

  const renderSnapshot = (snap: ProxyAggregateSnapshot) => {
    if (!snap.aggregate || snap.aggregate.replicaCount === 0) {
      mount(summary, []);
      mount(list, [el('div', { class: 'empty', text: 'No proxy replicas discovered. Check PROXY_COMPOSE_PROJECT / PROXY_COMPOSE_SERVICE.' })]);
      status.textContent = `No replicas · ${formatDateTime(snap.scrapedAt)}`;
      return;
    }
    const agg = snap.aggregate;
    // A rolling redeploy intentionally produces a transient where N+1 proxy
    // containers coexist and a freshly booted replica is not yet serving
    // /api/metrics (it gates on /api/health post-migration). That partial
    // mismatch is expected, not a fault — only zero reachable replicas is a
    // genuine cluster outage.
    const reachableTone =
      agg.reachable === agg.replicaCount ? 'ok' :
        agg.reachable === 0 ? 'critical' : 'rolling';
    const loopMs = agg.maxEventLoopP99Seconds * 1000;
    const loopPct = Math.min(100, (loopMs / 200) * 100); // 200ms = full bar
    const waitingPct = Math.min(100, agg.totalDbPoolWaiting * 25); // any waiting is bad
    mount(summary, [
      infoTile(
        'Replicas',
        `${agg.reachable} / ${agg.replicaCount}`,
        reachableTone === 'ok' ? 'All replicas reachable' :
          reachableTone === 'critical' ? 'No replicas responding' :
            'Rolling/draining — some replicas not yet serving metrics (expected during a deploy)',
      ),
      infoTile(
        'Requests (since start)',
        agg.totalRequests.toLocaleString(),
        `mean p95 ${formatMs(agg.meanP95Seconds)} · max p99 ${formatMs(agg.maxP99Seconds)}`,
      ),
      gauge(
        'Event-loop p99',
        loopPct,
        formatMs(agg.maxEventLoopP99Seconds),
        'Worst replica · target < 100 ms',
      ),
      gauge(
        'DB pool waiting',
        waitingPct,
        String(agg.totalDbPoolWaiting),
        `Cluster total · ${agg.totalDbPoolTotal} connections open`,
      ),
      infoTile(
        'Total RSS',
        formatBytes(agg.totalRssBytes),
        'Sum of resident memory across replicas',
      ),
    ]);

    const rows = snap.replicas.map((r) => renderReplicaCard(r));
    mount(list, rows.length > 0 ? rows : [el('div', { class: 'empty', text: 'No replicas in snapshot.' })]);
    status.textContent = `Live · ${snap.replicas.length} replicas · ${formatDateTime(snap.scrapedAt)}`;
  };

  const close = deps.api.streamProxyMetrics({
    onSnapshot: renderSnapshot,
    onError: () => { status.textContent = 'Disconnected — retrying…'; },
  });

  return { el: card, cleanup: close };
}

function renderReplicaCard(r: ProxyReplicaSnapshot): HTMLElement {
  const idx = r.identity.replicaIndex;
  const title = r.identity.containerName +
    (typeof idx === 'number' ? ` · #${idx}` : '');
  const kind: PillKind =
    r.identity.state === 'running' ? (r.ok ? 'running' : 'warning') :
      r.identity.state === 'restarting' ? 'warning' :
        'idle';
  const head = el('div', {
    class: 'container-row-head',
    children: [
      statusPill(kind, r.identity.state),
      el('span', { class: 'container-name', text: title }),
      el('span', { class: 'container-image', text: r.identity.ip ?? '(no ip)' }),
    ],
  });

  let stats: HTMLElement;
  if (!r.ok || !r.metrics) {
    stats = el('div', {
      class: 'container-stats',
      children: [
        kv('Status', r.error ?? 'unreachable'),
        kv('Scrape', `${r.scrapeDurationMs} ms`),
      ],
    });
  } else {
    const m = r.metrics;
    stats = el('div', {
      class: 'container-stats',
      children: [
        kv('Requests', m.http.totalRequests.toLocaleString()),
        kv('p50/p95/p99',
          `${formatMs(m.http.p50Seconds)} / ${formatMs(m.http.p95Seconds)} / ${formatMs(m.http.p99Seconds)}`,
        ),
        kv('Errors', formatPercent(m.http.errorRatio * 100)),
        kv('Loop p99', formatMs(m.eventLoop.p99Seconds)),
        kv('DB pool',
          `${m.dbPool.idle}/${m.dbPool.total} idle · ${m.dbPool.waiting} waiting`,
        ),
        kv('RSS', formatBytes(m.process.residentMemoryBytes)),
        kv('Heap', `${formatBytes(m.process.heapUsedBytes)} / ${formatBytes(m.process.heapTotalBytes)}`),
        kv('Uptime', formatUptime(m.process.uptimeSeconds)),
      ],
    });
  }

  const meta = el('div', {
    class: 'container-meta',
    text: r.metrics?.replicaId ?? r.identity.containerName,
  });

  const children: HTMLElement[] = [head, stats, meta];
  if (r.ok && r.metrics && r.metrics.http.topRoutes.length > 0) {
    children.push(renderTopRoutesTable(r.metrics.http.topRoutes));
  }
  return el('div', { class: 'container-row', children });
}

function renderTopRoutesTable(routes: readonly ProxyHttpRouteSummary[]): HTMLElement {
  const rows: HTMLElement[] = [];
  rows.push(el('div', {
    class: 'route-row route-head',
    children: [
      el('span', { text: 'Route' }),
      el('span', { text: 'Count' }),
      el('span', { text: 'p95' }),
      el('span', { text: 'Errors' }),
    ],
  }));
  for (const r of routes) {
    rows.push(el('div', {
      class: 'route-row',
      children: [
        el('span', { text: `${r.method} ${r.route}` }),
        el('span', { text: r.count.toLocaleString() }),
        el('span', { text: formatMs(r.p95Seconds) }),
        el('span', { text: formatPercent(r.errorRatio * 100) }),
      ],
    }));
  }
  return el('div', { class: 'route-table', children: rows });
}

function formatMs(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0 ms';
  if (seconds < 0.001) return `${(seconds * 1e6).toFixed(0)} µs`;
  if (seconds < 1) return `${(seconds * 1000).toFixed(seconds < 0.01 ? 2 : 1)} ms`;
  return `${seconds.toFixed(2)} s`;
}

// ─── Containers ──────────────────────────────────────────────────

function renderContainers(deps: MonitoringViewDeps): { el: HTMLElement; cleanup: () => void } {
  const list = el('div', { class: 'container-list' });
  const status = el('span', { class: 'who', text: 'Connecting…' });
  const card = el('div', {
    class: 'card',
    children: [
      el('div', {
        class: 'toolbar',
        children: [
          el('h2', { text: 'Containers', attrs: { style: 'margin:0' } }),
          el('span', { class: 'spacer' }),
          status,
        ],
      }),
      list,
    ],
  });

  const containerKind = (state: string): PillKind => {
    if (state === 'running') return 'running';
    if (state === 'paused' || state === 'restarting') return 'warning';
    if (state === 'exited' || state === 'dead' || state === 'created') return 'idle';
    return 'idle';
  };

  const renderSnapshot = (data: { containers: ContainerSummary[]; stats: ContainerStats[]; timestamp: number }) => {
    const statsById = new Map<string, ContainerStats>();
    for (const s of data.stats) statsById.set(s.id, s);
    if (data.containers.length === 0) {
      mount(list, [el('div', { class: 'empty', text: 'No containers found.' })]);
    } else {
      mount(list, data.containers.map((c) => renderContainerRow(deps, c, statsById.get(c.id), containerKind(c.state))));
    }
    status.textContent = `Live · ${data.containers.length} containers · ${formatDateTime(data.timestamp)}`;
  };

  const close = deps.api.streamContainers({
    onSnapshot: renderSnapshot,
    onError: () => { status.textContent = 'Disconnected — retrying…'; },
  });

  return { el: card, cleanup: close };
}

function renderContainerRow(
  deps: MonitoringViewDeps,
  c: ContainerSummary,
  s: ContainerStats | undefined,
  kind: PillKind,
): HTMLElement {
  const name = c.names[0] ?? c.id.slice(0, 12);
  const cpuTxt = s ? formatPercent(s.cpuPercent) : '–';
  const memTxt = s
    ? `${formatBytes(s.memUsage)} / ${formatBytes(s.memLimit)} (${formatPercent(s.memPercent)})`
    : '–';
  const netTxt = s ? `${formatBytes(s.netRx)} ↓ · ${formatBytes(s.netTx)} ↑` : '–';
  const ioTxt = s ? `${formatBytes(s.blockRead)} R · ${formatBytes(s.blockWrite)} W` : '–';

  const head = el('div', {
    class: 'container-row-head',
    children: [
      statusPill(kind, c.state),
      el('span', { class: 'container-name', text: name }),
      el('span', { class: 'container-image', text: c.image }),
    ],
  });
  const stats = el('div', {
    class: 'container-stats',
    children: [
      kv('CPU', cpuTxt),
      kv('Mem', memTxt),
      kv('Net', netTxt),
      kv('I/O', ioTxt),
      kv('PIDs', s ? String(s.pids) : '–'),
    ],
  });
  const meta = el('div', {
    class: 'container-meta',
    text: c.status + (c.ports.length > 0 ? ` · ${c.ports.join(', ')}` : ''),
  });

  const logsBtn = el('button', {
    text: 'Logs',
    on: {
      click: () => openLogsDrawer(deps, c.id, name),
    },
  });
  const actions = el('div', { class: 'container-actions', children: [logsBtn] });

  return el('div', {
    class: 'container-row',
    children: [head, stats, meta, actions],
  });
}

function kv(k: string, v: string): HTMLElement {
  return el('div', {
    class: 'kv',
    children: [
      el('span', { class: 'kv-key', text: k }),
      el('span', { class: 'kv-value', text: v }),
    ],
  });
}

function openLogsDrawer(deps: MonitoringViewDeps, id: string, name: string): void {
  const backdrop = el('div', { class: 'modal-backdrop' });
  const closeBtn = el('button', { class: 'ghost', text: 'Close' });
  const followBtn = el('button', { class: 'ghost', text: 'Following ▾', attrs: { 'data-follow': 'true' } });
  const pre = el('pre', { class: 'log-output' });
  const titleSpan = el('h3', { text: `${name} · live logs` });
  const subtitle = el('span', { class: 'who', text: 'connecting…' });
  const modal = el('div', {
    class: 'modal modal-wide',
    children: [
      el('header', {
        children: [
          el('div', { class: 'modal-title', children: [titleSpan, subtitle] }),
          el('div', { class: 'log-actions', children: [followBtn, closeBtn] }),
        ],
      }),
      pre,
    ],
  });
  backdrop.append(modal);
  document.body.append(backdrop);

  let follow = true;
  followBtn.addEventListener('click', () => {
    follow = !follow;
    followBtn.textContent = follow ? 'Following ▾' : 'Paused';
    if (follow) pre.scrollTop = pre.scrollHeight;
  });
  pre.addEventListener('scroll', () => {
    const nearBottom = pre.scrollHeight - pre.scrollTop - pre.clientHeight < 40;
    if (!nearBottom && follow) {
      follow = false;
      followBtn.textContent = 'Paused';
    }
  });

  const close = deps.api.streamContainerLogs(id, {
    onLog: (chunk) => {
      pre.append(document.createTextNode(chunk));
      if (follow) pre.scrollTop = pre.scrollHeight;
      subtitle.textContent = 'live · last 1500 lines + tail';
    },
    onEnd: (code) => {
      subtitle.textContent = `stream ended (exit ${code})`;
    },
    onError: () => {
      subtitle.textContent = 'connection lost';
    },
  });

  const dismiss = () => {
    close();
    backdrop.remove();
    document.removeEventListener('keydown', onKey);
  };
  closeBtn.addEventListener('click', dismiss);
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) dismiss();
  });
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') dismiss();
  };
  document.addEventListener('keydown', onKey);
}

// ─── Images ──────────────────────────────────────────────────────

function renderImages(deps: MonitoringViewDeps): { el: HTMLElement; cleanup: () => void } {
  const list = el('div', { class: 'volume-list' });
  const refreshBtn = el('button', { class: 'ghost', text: 'Refresh' });
  const pruneBtn = el('button', {
    class: 'danger',
    text: 'Prune dangling',
    attrs: { disabled: true, title: 'docker image prune — removes all untagged images' },
  });
  const summary = el('span', { class: 'who' });
  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });
  const dfGrid = el('div', { class: 'df-grid' });
  const dfCard = el('div', {
    class: 'card',
    children: [
      el('div', {
        class: 'toolbar',
        children: [
          el('h2', { text: 'Storage breakdown', attrs: { style: 'margin:0' } }),
          el('span', { class: 'spacer' }),
          el('span', { class: 'who', text: 'docker system df' }),
        ],
      }),
      dfGrid,
    ],
  });

  const showAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    alertBox.className = kind === 'success' ? 'alert success' : 'alert';
    alertBox.textContent = msg;
    alertBox.removeAttribute('hidden');
    setTimeout(() => alertBox.setAttribute('hidden', ''), 6000);
  };

  pruneBtn.addEventListener('click', async () => {
    if (!confirm(pruneBtn.title || 'Prune all dangling images?')) return;
    pruneBtn.setAttribute('disabled', '');
    const oldText = pruneBtn.textContent;
    pruneBtn.textContent = 'Pruning…';
    try {
      const res = await deps.api.pruneImages();
      const count = res.deleted.length;
      showAlert(
        count === 0
          ? 'Nothing to prune.'
          : `Pruned ${count} dangling image${count > 1 ? 's' : ''} · freed ${formatBytes(res.reclaimedBytes)}`,
        'success',
      );
      await refresh();
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Prune failed');
      pruneBtn.removeAttribute('disabled');
      pruneBtn.textContent = oldText ?? 'Prune dangling';
    }
  });

  const imagesCard = el('div', {
    class: 'card',
    children: [
      el('div', {
        class: 'toolbar',
        children: [
          el('h2', { text: 'Docker images', attrs: { style: 'margin:0' } }),
          el('span', { class: 'spacer' }),
          summary,
          pruneBtn,
          refreshBtn,
        ],
      }),
      alertBox,
      list,
    ],
  });
  const card = el('div', { children: [dfCard, imagesCard] });

  const dfLabels: Record<string, { label: string; hint: string }> = {
    images: { label: 'Images', hint: 'tagged + dangling layers' },
    containers: { label: 'Containers', hint: 'writable layers above each image' },
    volumes: { label: 'Volumes', hint: 'docker volume create' },
    buildCache: { label: 'Build cache', hint: 'BuildKit + legacy intermediates' },
  };

  const renderDf = async () => {
    try {
      const df = await deps.api.systemDf();
      mount(
        dfGrid,
        df.categories.map((c) => {
          const meta = dfLabels[c.type] ?? { label: c.type, hint: '' };
          const tile = el('div', { class: 'df-tile' });
          tile.append(
            el('div', { class: 'df-tile-head', children: [
              el('span', { class: 'df-tile-label', text: meta.label }),
              el('span', { class: 'df-tile-count', text: `${c.total} total · ${c.inactive} unused` }),
            ] }),
            el('div', { class: 'df-tile-size', text: formatBytes(c.active) }),
            el('div', {
              class: 'df-tile-sub',
              text: c.reclaimable > 0
                ? `${formatBytes(c.reclaimable)} reclaimable · ${meta.hint}`
                : meta.hint,
            }),
          );
          if (c.reclaimable > 0) {
            const action = pruneAction(c.type);
            if (action) {
              const btn = el('button', {
                class: 'danger df-tile-btn',
                text: `Prune ${formatBytes(c.reclaimable)}`,
                attrs: { title: action.title },
                on: {
                  click: async () => {
                    if (!confirm(action.confirm)) return;
                    btn.setAttribute('disabled', '');
                    btn.textContent = 'Pruning…';
                    try {
                      const reclaimed = await action.run();
                      showAlert(`${meta.label}: freed ${formatBytes(reclaimed)}`, 'success');
                      await renderDf();
                      await refresh();
                    } catch (err) {
                      btn.removeAttribute('disabled');
                      btn.textContent = `Prune ${formatBytes(c.reclaimable)}`;
                      showAlert(err instanceof ApiError ? err.message : 'Prune failed');
                    }
                  },
                },
              });
              tile.append(btn);
            }
          }
          return tile;
        }),
      );
    } catch (err) {
      mount(dfGrid, [
        el('div', {
          class: 'empty',
          text: err instanceof ApiError ? err.message : 'Failed to load docker df',
        }),
      ]);
    }
  };

  const pruneAction = (
    type: 'images' | 'containers' | 'volumes' | 'buildCache',
  ): { run: () => Promise<number>; confirm: string; title: string } | null => {
    if (type === 'images') {
      return {
        // `all=true` matches the "reclaimable" number shown on the tile —
        // i.e. every image not currently used by a container, including
        // tagged ones (e.g. base images like nvidia/cuda). Future builds
        // re-pull what they need; no other side effect.
        run: async () => (await deps.api.pruneImages(true)).reclaimedBytes,
        confirm:
          'Remove every image not currently used by a container?\n\n' +
          'This includes tagged base images (nvidia/cuda, etc.) — they will be re-pulled on the next build.',
        title: 'docker image prune -a',
      };
    }
    if (type === 'containers') {
      return {
        run: async () => (await deps.api.pruneContainers()).reclaimedBytes,
        confirm: 'Remove every stopped container and its writable layer?',
        title: 'docker container prune',
      };
    }
    if (type === 'buildCache') {
      return {
        run: async () => (await deps.api.pruneBuildCache(false)).reclaimedBytes,
        confirm:
          'Remove every unused build cache record? Future builds will re-download / re-compile from scratch (slower first build, no other side effect).',
        title: 'docker builder prune',
      };
    }
    // Volumes prune is dangerous (risks losing app data) — leave it manual.
    return null;
  };

  const renderRow = (img: ImageSummary, refresh: () => Promise<void>) => {
    const idShort = img.id.replace(/^sha256:/, '').slice(0, 12);
    const tagLine = img.dangling
      ? '<dangling>'
      : img.repoTags.join(', ');

    const kind: PillKind =
      img.dangling ? 'warning' : img.containers > 0 ? 'running' : 'idle';
    const pillLabel = img.dangling
      ? 'dangling'
      : img.containers > 0
        ? `${img.containers} in use`
        : 'idle';

    // Prefer the first repo:tag for the delete ref so the daemon untags
    // cleanly. Fall back to the image id when there are no tags.
    const ref = img.repoTags[0] ?? idShort;

    const deleteImage = async (force: boolean) => {
      const label = force ? 'force-delete' : 'delete';
      if (!confirm(`docker rmi${force ? ' -f' : ''} ${ref}?`)) return;
      try {
        await deps.api.removeImage(ref, force);
        showAlert(`Image ${label}d (${ref})`, 'success');
        await refresh();
      } catch (err) {
        if (err instanceof ApiError && err.code === 'image_in_use' && !force) {
          if (confirm(`${err.message}\n\nForce delete anyway?`)) {
            await deleteImage(true);
            return;
          }
        }
        showAlert(err instanceof ApiError ? err.message : `Failed to ${label} image`);
      }
    };

    const deleteBtn = el('button', {
      class: 'danger',
      text: 'Delete',
      on: { click: () => void deleteImage(false) },
    });
    const forceBtn = el('button', {
      class: 'danger',
      text: 'Force',
      attrs: { title: 'docker rmi -f' },
      on: { click: () => void deleteImage(true) },
    });

    return el('div', {
      class: 'volume-row',
      children: [
        el('div', {
          class: 'volume-head',
          children: [
            statusPill(kind, pillLabel),
            el('span', { class: 'volume-name', text: tagLine }),
            el('span', { class: 'volume-size', text: formatBytes(img.size) }),
          ],
        }),
        el('div', {
          class: 'volume-meta',
          text: `${idShort} · created ${formatRelativeTime(img.createdAt)}${
            img.repoDigests.length > 0 ? ` · ${img.repoDigests.length} digest(s)` : ''
          }`,
        }),
        el('div', { class: 'volume-actions', children: [deleteBtn, forceBtn] }),
      ],
    });
  };

  const refresh = async () => {
    refreshBtn.setAttribute('disabled', '');
    try {
      const { items } = await deps.api.listImages();
      // Sort by size descending — surfaces the heavy images first, which is
      // exactly what the user is here to clean up.
      const sorted = [...items].sort((a, b) => b.size - a.size);
      const totalSize = sorted.reduce((acc, i) => acc + i.size, 0);
      const dangling = sorted.filter((i) => i.dangling);
      const danglingSize = dangling.reduce((acc, i) => acc + i.size, 0);
      summary.textContent = sorted.length
        ? `${sorted.length} images · ${formatBytes(totalSize)} total${
            dangling.length > 0 ? ` · ${dangling.length} dangling (${formatBytes(danglingSize)})` : ''
          }`
        : '';
      if (dangling.length > 0) {
        pruneBtn.removeAttribute('disabled');
        pruneBtn.textContent = `Prune ${dangling.length} dangling (${formatBytes(danglingSize)})`;
        pruneBtn.title = `docker image prune -f — removes ${dangling.length} untagged image${dangling.length > 1 ? 's' : ''} reclaiming ${formatBytes(danglingSize)}`;
      } else {
        pruneBtn.setAttribute('disabled', '');
        pruneBtn.textContent = 'Prune dangling';
        pruneBtn.title = 'No dangling images to prune';
      }
      if (sorted.length === 0) {
        mount(list, [el('div', { class: 'empty', text: 'No images on this host.' })]);
        return;
      }
      mount(list, sorted.map((i) => renderRow(i, refresh)));
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Failed to load images');
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };

  refreshBtn.addEventListener('click', () => {
    void refresh();
    void renderDf();
  });
  void renderDf();
  void refresh();

  return { el: card, cleanup: () => { /* no streams */ } };
}

// ─── Volumes ─────────────────────────────────────────────────────

function renderVolumes(deps: MonitoringViewDeps): { el: HTMLElement; cleanup: () => void } {
  const list = el('div', { class: 'volume-list' });
  const refreshBtn = el('button', { class: 'ghost', text: 'Refresh' });
  const summary = el('span', { class: 'who' });
  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });

  const showAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    alertBox.className = kind === 'success' ? 'alert success' : 'alert';
    alertBox.textContent = msg;
    alertBox.removeAttribute('hidden');
    setTimeout(() => alertBox.setAttribute('hidden', ''), 6000);
  };

  const card = el('div', {
    class: 'card',
    children: [
      el('div', {
        class: 'toolbar',
        children: [
          el('h2', { text: 'Docker volumes', attrs: { style: 'margin:0' } }),
          el('span', { class: 'spacer' }),
          summary,
          refreshBtn,
        ],
      }),
      alertBox,
      list,
    ],
  });

  const renderRow = (v: VolumeSummary) => {
    const sizeTxt = v.size >= 0 ? formatBytes(v.size) : 'n/a';
    const refKind: PillKind = v.refCount > 0 ? 'running' : 'idle';
    const refLabel = v.refCount > 0 ? `${v.refCount} in use` : 'unused';

    const downloadLink = el('a', {
      class: 'button',
      text: 'Download backup',
      attrs: {
        href: deps.api.volumeBackupUrl(v.name),
        download: `${v.name}.tar.gz`,
      },
    });
    const fileInput = el('input', {
      attrs: { type: 'file', accept: '.tar.gz,.tgz,application/gzip', style: 'display:none' },
    });
    const restoreBtn = el('button', {
      class: 'danger',
      text: 'Restore from backup',
    });
    restoreBtn.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', async () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      if (!confirm(`Restore "${v.name}" from ${file.name}?\n\nALL CURRENT CONTENT IN THIS VOLUME WILL BE OVERWRITTEN.`)) {
        fileInput.value = '';
        return;
      }
      restoreBtn.setAttribute('disabled', '');
      restoreBtn.textContent = 'Restoring…';
      try {
        await deps.api.restoreVolume(v.name, file);
        showAlert(`Volume "${v.name}" restored from ${file.name}.`, 'success');
      } catch (err) {
        showAlert(err instanceof ApiError ? err.message : 'Restore failed');
      } finally {
        restoreBtn.removeAttribute('disabled');
        restoreBtn.textContent = 'Restore from backup';
        fileInput.value = '';
      }
    });

    return el('div', {
      class: 'volume-row',
      children: [
        el('div', {
          class: 'volume-head',
          children: [
            statusPill(refKind, refLabel),
            el('span', { class: 'volume-name', text: v.name }),
            el('span', { class: 'volume-size', text: sizeTxt }),
          ],
        }),
        el('div', {
          class: 'volume-meta',
          text: `${v.driver} · ${v.scope} · ${v.mountpoint}${v.createdAt ? ` · created ${v.createdAt}` : ''}`,
        }),
        el('div', { class: 'volume-actions', children: [downloadLink, restoreBtn, fileInput] }),
      ],
    });
  };

  const refresh = async () => {
    refreshBtn.setAttribute('disabled', '');
    try {
      const { items } = await deps.api.listVolumes();
      summary.textContent = items.length ? `${items.length} volumes` : '';
      if (items.length === 0) {
        mount(list, [el('div', { class: 'empty', text: 'No volumes.' })]);
        return;
      }
      mount(list, items.map(renderRow));
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Failed to load volumes');
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };

  refreshBtn.addEventListener('click', refresh);
  void refresh();

  return { el: card, cleanup: () => { /* nothing streamed */ } };
}
